import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, Trash2, GripVertical, Edit2, Save, X, RefreshCw, Zap, 
  ArrowRightLeft, CheckCircle, XCircle, AlertCircle, Settings2,
  ChevronRight, Palette, Download, CloudDownload
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const STAGE_COLORS = [
  '#60A5FA', '#818CF8', '#A78BFA', '#F59E0B', '#10B981', 
  '#06B6D4', '#8B5CF6', '#22C55E', '#EF4444', '#EC4899',
  '#14B8A6', '#F97316', '#6366F1', '#84CC16', '#6B7280'
];

const PipelineConfigPage = () => {
  const { user, selectedBusiness } = useAuth();
  const businessId = selectedBusiness?.business_id || user?.business_id;

  const [pipelines, setPipelines] = useState([]);
  const [ghlPipelines, setGhlPipelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showAddPipeline, setShowAddPipeline] = useState(false);
  const [showAddStage, setShowAddStage] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [ghlConnected, setGhlConnected] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedImports, setSelectedImports] = useState({});

  const [newPipeline, setNewPipeline] = useState({
    name: '', pipeline_type: 'active', ghl_pipeline_id: '', stages: []
  });

  const [newStage, setNewStage] = useState({
    name: '', color: '#60A5FA', ghl_stage_id: '',
    is_won_stage: false, is_lost_stage: false,
    requires_manual_followup: false, auto_assign_to_human: false
  });

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 
      'Content-Type': 'application/json', 
      ...(token ? { Authorization: `Bearer ${token}` } : {}) 
    };
  }, []);

  const fetchPipelines = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ghl/pipelines`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setPipelines(data);
        if (data.length > 0 && !selectedPipeline) {
          setSelectedPipeline(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId, authHeaders, selectedPipeline]);

  const checkGhlConnection = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ghl/credentials`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setGhlConnected(data?.is_connected || false);
      }
    } catch (error) {
      setGhlConnected(false);
    }
  }, [authHeaders]);

  const fetchRemotePipelines = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/ghl/fetch-remote-pipelines`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const remotePipelines = data.pipelines || [];
        setGhlPipelines(remotePipelines);
        
        // Mark which are already imported locally
        const localGhlIds = new Set(pipelines.map(p => p.ghl_pipeline_id).filter(Boolean));
        const initSelected = {};
        remotePipelines.forEach(p => {
          initSelected[p.id] = false;
          p._alreadyImported = localGhlIds.has(p.id);
        });
        setSelectedImports(initSelected);
        setShowImportModal(true);
        toast.success(`Found ${remotePipelines.length} pipeline(s) in GHL`);
      } else {
        toast.error('Failed to fetch pipelines from GHL');
      }
    } catch (error) {
      toast.error('Failed to fetch from GHL');
    } finally {
      setSyncing(false);
    }
  };

  const handleImportSelected = async () => {
    const toImport = ghlPipelines.filter(p => selectedImports[p.id] && !p._alreadyImported);
    if (toImport.length === 0) {
      toast.error('No new pipelines selected to import');
      return;
    }
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/ghl/import-pipelines`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ pipelines: toImport }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowImportModal(false);
        setSelectedPipeline(null);
        fetchPipelines();
      } else {
        toast.error('Failed to import pipelines');
      }
    } catch (error) {
      toast.error('Failed to import pipelines');
    } finally {
      setImporting(false);
    }
  };

  const handleDeletePipeline = async (pipelineId) => {
    if (!confirm('Delete this pipeline and all its stages? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/ghl/pipelines/${pipelineId}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        toast.success('Pipeline deleted');
        if (selectedPipeline?.pipeline_id === pipelineId) {
          setSelectedPipeline(null);
        }
        fetchPipelines();
      } else {
        toast.error('Failed to delete pipeline');
      }
    } catch (error) {
      toast.error('Failed to delete pipeline');
    } finally {
      setDeleting(false);
    }
  };

  const getGhlStageOptions = () => {
    const options = [];
    for (const pipeline of ghlPipelines) {
      for (const stage of (pipeline.stages || [])) {
        options.push({
          value: stage.id, label: `${pipeline.name} > ${stage.name}`,
          pipeline_id: pipeline.id, pipeline_name: pipeline.name, stage_name: stage.name
        });
      }
    }
    return options;
  };

  useEffect(() => {
    fetchPipelines();
    checkGhlConnection();
  }, [fetchPipelines, checkGhlConnection]);

  const handleCreatePipeline = async () => {
    if (!newPipeline.name.trim()) { toast.error('Pipeline name is required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/ghl/pipelines`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify(newPipeline),
      });
      if (res.ok) {
        toast.success('Pipeline created');
        setShowAddPipeline(false);
        setNewPipeline({ name: '', pipeline_type: 'active', ghl_pipeline_id: '', stages: [] });
        fetchPipelines();
      } else { toast.error('Failed to create pipeline'); }
    } catch (error) { toast.error('Failed to create pipeline'); }
  };

  const handleUpdatePipeline = async () => {
    if (!selectedPipeline) return;
    try {
      const res = await fetch(`${API_URL}/api/ghl/pipelines/${selectedPipeline.pipeline_id}`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify(selectedPipeline),
      });
      if (res.ok) {
        toast.success('Pipeline updated');
        setEditMode(false);
        fetchPipelines();
      } else { toast.error('Failed to update pipeline'); }
    } catch (error) { toast.error('Failed to update pipeline'); }
  };

  const handleAddStage = () => {
    if (!newStage.name.trim()) { toast.error('Stage name is required'); return; }
    const stage = {
      stage_id: `stg_${Date.now()}`, name: newStage.name,
      ghl_stage_id: newStage.ghl_stage_id || null, ghl_stage_name: null,
      color: newStage.color, position: selectedPipeline.stages.length,
      is_won_stage: newStage.is_won_stage, is_lost_stage: newStage.is_lost_stage,
      requires_manual_followup: newStage.requires_manual_followup,
      auto_assign_to_human: newStage.auto_assign_to_human
    };
    setSelectedPipeline(prev => ({ ...prev, stages: [...prev.stages, stage] }));
    setShowAddStage(false);
    setNewStage({ name: '', color: '#60A5FA', ghl_stage_id: '', is_won_stage: false, is_lost_stage: false, requires_manual_followup: false, auto_assign_to_human: false });
  };

  const handleRemoveStage = (stageId) => {
    setSelectedPipeline(prev => ({
      ...prev, stages: prev.stages.filter(s => s.stage_id !== stageId)
    }));
  };

  const handleUpdateStage = (stageId, field, value) => {
    setSelectedPipeline(prev => ({
      ...prev, stages: prev.stages.map(s => s.stage_id === stageId ? { ...s, [field]: value } : s)
    }));
  };

  const toggleImportSelection = (pipelineId) => {
    setSelectedImports(prev => ({ ...prev, [pipelineId]: !prev[pipelineId] }));
  };

  return (
    <div className="space-y-6" data-testid="pipeline-config-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pipeline Configuration</h1>
          <p className="text-slate-500">Manage pipelines and stages, map to GHL</p>
        </div>
        <div className="flex items-center gap-2">
          {ghlConnected && (
            <Button variant="outline" onClick={fetchRemotePipelines} disabled={syncing} data-testid="import-from-ghl-btn">
              <CloudDownload className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Import from GHL
            </Button>
          )}
          <Button variant="outline" onClick={() => window.location.href = '/custom-fields'}>
            Custom Fields
          </Button>
          <Button onClick={() => setShowAddPipeline(true)} data-testid="add-pipeline-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Pipeline
          </Button>
        </div>
      </div>

      {/* GHL Status */}
      {!ghlConnected && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">GHL Not Connected</p>
                <p className="text-sm text-amber-600">Connect GHL in Settings to sync pipelines and stages</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Pipelines</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : pipelines.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Settings2 className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                  <p>No pipelines yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {pipelines.map((pipeline) => (
                    <div
                      key={pipeline.pipeline_id}
                      className={`w-full p-4 text-left transition-colors hover:bg-slate-50 cursor-pointer ${
                        selectedPipeline?.pipeline_id === pipeline.pipeline_id ? 'bg-cyan-50 border-l-2 border-cyan-500' : ''
                      }`}
                      onClick={() => { setSelectedPipeline(pipeline); setEditMode(false); }}
                      data-testid={`pipeline-item-${pipeline.pipeline_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900">{pipeline.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="text-[10px]" variant="secondary">
                              {pipeline.stages?.length || 0} stages
                            </Badge>
                            <Badge className={`text-[10px] ${
                              pipeline.pipeline_type === 'active' ? 'bg-green-100 text-green-700' :
                              pipeline.pipeline_type === 'manual_followup' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {pipeline.pipeline_type}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleDeletePipeline(pipeline.pipeline_id); }}
                            disabled={deleting}
                            data-testid={`delete-pipeline-${pipeline.pipeline_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                      {pipeline.ghl_pipeline_id && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-orange-600">
                          <Zap className="w-3 h-3" />
                          Linked to GHL
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Pipeline Details */}
        <Card className="lg:col-span-2">
          {selectedPipeline ? (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    {editMode ? (
                      <Input
                        value={selectedPipeline.name}
                        onChange={(e) => setSelectedPipeline(prev => ({ ...prev, name: e.target.value }))}
                        className="text-lg font-semibold"
                      />
                    ) : (
                      <CardTitle>{selectedPipeline.name}</CardTitle>
                    )}
                    <CardDescription>
                      {selectedPipeline.stages?.length || 0} stages | {selectedPipeline.pipeline_type}
                      {selectedPipeline.ghl_pipeline_id && (
                        <span className="ml-2 text-orange-600">
                          <Zap className="w-3 h-3 inline mr-1" />GHL linked
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {editMode ? (
                      <>
                        <Button variant="outline" size="sm" onClick={() => { setEditMode(false); fetchPipelines(); }}>
                          <X className="w-4 h-4 mr-1" />Cancel
                        </Button>
                        <Button size="sm" onClick={handleUpdatePipeline}>
                          <Save className="w-4 h-4 mr-1" />Save
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-pipeline-btn">
                        <Edit2 className="w-4 h-4 mr-1" />Edit
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* GHL Mapping */}
                {editMode && ghlConnected && ghlPipelines.length > 0 && (
                  <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <Label className="text-sm font-medium text-orange-800">Link to GHL Pipeline</Label>
                    <Select
                      value={selectedPipeline.ghl_pipeline_id || ''}
                      onValueChange={(val) => setSelectedPipeline(prev => ({ ...prev, ghl_pipeline_id: val }))}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select GHL pipeline" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value=" ">None</SelectItem>
                        {ghlPipelines.map((gp) => (
                          <SelectItem key={gp.id} value={gp.id}>{gp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Pipeline Type (edit mode) */}
                {editMode && (
                  <div className="mb-4">
                    <Label>Pipeline Type</Label>
                    <Select
                      value={selectedPipeline.pipeline_type}
                      onValueChange={(val) => setSelectedPipeline(prev => ({ ...prev, pipeline_type: val }))}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active (main pipeline)</SelectItem>
                        <SelectItem value="manual_followup">Manual Follow-Up</SelectItem>
                        <SelectItem value="closed">Closed / Archive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Stages */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-slate-900">Stages</h4>
                    {editMode && (
                      <Button size="sm" variant="outline" onClick={() => setShowAddStage(true)}>
                        <Plus className="w-4 h-4 mr-1" />Add Stage
                      </Button>
                    )}
                  </div>

                  {selectedPipeline.stages?.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
                      <p>No stages yet</p>
                      {editMode && <p className="text-sm">Click "Add Stage" to create one</p>}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedPipeline.stages?.map((stage) => (
                        <div 
                          key={stage.stage_id}
                          className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg"
                        >
                          {editMode && <GripVertical className="w-4 h-4 text-slate-300 cursor-grab" />}
                          <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                          <div className="flex-1 min-w-0">
                            {editMode ? (
                              <Input value={stage.name} onChange={(e) => handleUpdateStage(stage.stage_id, 'name', e.target.value)} className="h-8" />
                            ) : (
                              <p className="font-medium text-slate-900">{stage.name}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {stage.is_won_stage && <Badge className="text-[10px] bg-green-100 text-green-700">Won</Badge>}
                              {stage.is_lost_stage && <Badge className="text-[10px] bg-red-100 text-red-700">Lost</Badge>}
                              {stage.requires_manual_followup && <Badge className="text-[10px] bg-amber-100 text-amber-700">Manual</Badge>}
                              {stage.ghl_stage_id && (
                                <Badge className="text-[10px] bg-orange-100 text-orange-700">
                                  <Zap className="w-2 h-2 mr-1" />GHL
                                </Badge>
                              )}
                            </div>
                          </div>
                          {editMode && (
                            <div className="flex items-center gap-2">
                              <input type="color" value={stage.color} onChange={(e) => handleUpdateStage(stage.stage_id, 'color', e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
                              <Button variant="ghost" size="sm" onClick={() => handleRemoveStage(stage.stage_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex items-center justify-center h-[500px] text-slate-400">
              <div className="text-center">
                <Settings2 className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p>Select a pipeline to view details</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Import from GHL Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudDownload className="w-5 h-5 text-orange-500" />
              Import Pipelines from GHL
            </DialogTitle>
            <DialogDescription>Select which GHL pipelines to import into Beluga Pro</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px] py-4">
            {ghlPipelines.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p>No pipelines found in GHL</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ghlPipelines.map((pipeline) => (
                  <div 
                    key={pipeline.id}
                    className={`p-4 border rounded-lg transition-colors ${
                      pipeline._alreadyImported 
                        ? 'bg-green-50 border-green-200 opacity-70' 
                        : selectedImports[pipeline.id] 
                          ? 'bg-cyan-50 border-cyan-300' 
                          : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`ghl-pipeline-${pipeline.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={pipeline._alreadyImported || selectedImports[pipeline.id] || false}
                        onCheckedChange={() => toggleImportSelection(pipeline.id)}
                        disabled={pipeline._alreadyImported}
                        className="mt-0.5"
                        data-testid={`ghl-pipeline-checkbox-${pipeline.id}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{pipeline.name}</p>
                          {pipeline._alreadyImported && (
                            <Badge className="text-[10px] bg-green-100 text-green-700">Already Imported</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {pipeline.stages?.length || 0} stages
                        </p>
                        {pipeline.stages?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {pipeline.stages.map((stage) => (
                              <Badge key={stage.id} variant="secondary" className="text-[10px]">
                                {stage.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
            <Button 
              onClick={handleImportSelected} 
              disabled={importing || Object.values(selectedImports).every(v => !v)}
              data-testid="confirm-import-pipelines-btn"
            >
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Import Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Pipeline Dialog */}
      <Dialog open={showAddPipeline} onOpenChange={setShowAddPipeline}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Pipeline</DialogTitle>
            <DialogDescription>Add a new pipeline for managing leads</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Pipeline Name</Label>
              <Input placeholder="e.g., Active Leads" value={newPipeline.name}
                onChange={(e) => setNewPipeline(prev => ({ ...prev, name: e.target.value }))} className="mt-1.5" />
            </div>
            <div>
              <Label>Pipeline Type</Label>
              <Select value={newPipeline.pipeline_type}
                onValueChange={(val) => setNewPipeline(prev => ({ ...prev, pipeline_type: val }))}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active (main pipeline)</SelectItem>
                  <SelectItem value="manual_followup">Manual Follow-Up</SelectItem>
                  <SelectItem value="closed">Closed / Archive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {ghlConnected && ghlPipelines.length > 0 && (
              <div>
                <Label>Link to GHL Pipeline</Label>
                <Select value={newPipeline.ghl_pipeline_id}
                  onValueChange={(val) => setNewPipeline(prev => ({ ...prev, ghl_pipeline_id: val }))}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select GHL pipeline (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">None</SelectItem>
                    {ghlPipelines.map((gp) => (
                      <SelectItem key={gp.id} value={gp.id}>{gp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPipeline(false)}>Cancel</Button>
            <Button onClick={handleCreatePipeline} data-testid="create-pipeline-submit-btn">Create Pipeline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stage Dialog */}
      <Dialog open={showAddStage} onOpenChange={setShowAddStage}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Stage</DialogTitle>
            <DialogDescription>Add a new stage to {selectedPipeline?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Stage Name</Label>
              <Input placeholder="e.g., Qualified" value={newStage.name}
                onChange={(e) => setNewStage(prev => ({ ...prev, name: e.target.value }))} className="mt-1.5" />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input type="color" value={newStage.color}
                  onChange={(e) => setNewStage(prev => ({ ...prev, color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border-0" />
                <div className="flex gap-1">
                  {STAGE_COLORS.slice(0, 8).map((color) => (
                    <button key={color}
                      className={`w-6 h-6 rounded-full border-2 ${newStage.color === color ? 'border-slate-900' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewStage(prev => ({ ...prev, color }))} />
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {ghlConnected && ghlPipelines.length > 0 && (
                <div>
                  <Label>Link to GHL Stage</Label>
                  <Select value={newStage.ghl_stage_id}
                    onValueChange={(val) => {
                      const opt = getGhlStageOptions().find(o => o.value === val);
                      setNewStage(prev => ({ ...prev, ghl_stage_id: val, ghl_stage_name: opt?.stage_name || '' }));
                    }}>
                    <SelectTrigger className="mt-1.5" data-testid="ghl-stage-select">
                      <SelectValue placeholder="Select GHL stage (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value=" ">None</SelectItem>
                      {getGhlStageOptions().map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label>Won Stage</Label>
                <Switch checked={newStage.is_won_stage}
                  onCheckedChange={(val) => setNewStage(prev => ({ ...prev, is_won_stage: val, is_lost_stage: val ? false : prev.is_lost_stage }))} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Lost Stage</Label>
                <Switch checked={newStage.is_lost_stage}
                  onCheckedChange={(val) => setNewStage(prev => ({ ...prev, is_lost_stage: val, is_won_stage: val ? false : prev.is_won_stage }))} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Requires Manual Follow-up</Label>
                <Switch checked={newStage.requires_manual_followup}
                  onCheckedChange={(val) => setNewStage(prev => ({ ...prev, requires_manual_followup: val }))} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Auto-assign to Human</Label>
                <Switch checked={newStage.auto_assign_to_human}
                  onCheckedChange={(val) => setNewStage(prev => ({ ...prev, auto_assign_to_human: val }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStage(false)}>Cancel</Button>
            <Button onClick={handleAddStage}>Add Stage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PipelineConfigPage;
